import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore'; 
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { FaShareAlt, FaPrint, FaArrowLeft } from 'react-icons/fa';

import companyLogo from '../../assets/payment.png'; 
import signatureMorshed from '../../assets/morshed.png'; 
import signatureAnis from '../../assets/anis.png';     

const formatCurrency = (num) => {
  if (typeof num !== 'number') { num = parseFloat(num || 0); }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

function numberToWords(num) {
    const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
    const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
    const inWordsBDTInternal = (n) => {
        let str = '';
        if (n > 9999999) { str += inWordsBDTInternal(Math.floor(n / 10000000)); str += 'Crore '; n %= 10000000; }
        if (n > 99999) { str += inWordsBDTInternal(Math.floor(n / 100000)); str += 'Lakh '; n %= 100000; }
        if (n > 999) { str += inWordsBDTInternal(Math.floor(n / 1000)); str += 'Thousand '; n %= 1000; }
        if (n > 99) { str += inWordsBDTInternal(Math.floor(n / 100)); str += 'Hundred '; n %= 100; }
        if (n > 19) { str += b[Math.floor(n / 10)] + ' ' + a[n % 10]; } else { str += a[n]; }
        return str; 
    };
    const taka = Math.round(parseFloat(num || 0));
    if (taka === 0) return 'BDT ZERO TAKA ONLY';
    let takaWords = inWordsBDTInternal(taka).trim(); 
    let result = 'BDT ' + takaWords + ' Taka Only'; 
    return result.replace(/\s+/g, ' ').toUpperCase();
}

const ViewReceipt = () => {
    const { paymentId } = useParams(); 
    const navigate = useNavigate();
    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [liveInvoice, setLiveInvoice] = useState(null);
    const [isSharing, setIsSharing] = useState(false);

    useEffect(() => {
        const fetchReceipt = async () => {
            if (!paymentId) return;
            setLoading(true);
            setLiveInvoice(null); 
            try {
                const docRef = doc(db, "payments", paymentId);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                    const receiptData = docSnap.data();
                    setReceipt(receiptData);
                    
                    if (receiptData.invoiceDocId) {
                        const invoiceRef = doc(db, "invoices", receiptData.invoiceDocId);
                        const invoiceSnap = await getDoc(invoiceRef);
                        if (invoiceSnap.exists()) {
                            setLiveInvoice(invoiceSnap.data());
                        }
                    }
                } else {
                    console.log("No such receipt found!");
                }
            } catch (error) {
                console.error("Error fetching data: ", error);
            }
            setLoading(false);
        };
        fetchReceipt();
    }, [paymentId]);

    const handleShare = async () => {
        setIsSharing(true);
        try {
            const input = document.querySelector('.invoice-paper');
            if (!input) {
                alert("রিসিট পাওয়া যায়নি!");
                setIsSharing(false);
                return;
            }

            const canvas = await html2canvas(input, { 
                scale: 2, 
                useCORS: true,
                backgroundColor: '#ffffff',
                windowWidth: 1200 
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 0.8);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);

            const fileName = `Receipt_${receipt.receiptNo || 'Draft'}.pdf`;

            if (navigator.share) {
                const pdfBlob = pdf.output('blob');
                const file = new File([pdfBlob], fileName, { type: "application/pdf" });
                try {
                    await navigator.share({
                        files: [file],
                        title: 'Payment Receipt',
                        text: `Money Receipt #${receipt.receiptNo}`
                    });
                } catch (err) {
                    console.log("Share cancelled");
                }
            } else {
                pdf.save(fileName);
            }

        } catch (error) {
            console.error("Error sharing:", error);
            alert("পিডিএফ তৈরিতে সমস্যা হয়েছে।");
        } finally {
            setIsSharing(false);
        }
    };

    if (loading) return <p>Loading receipt...</p>;
    if (!receipt) return <div><p>Receipt not found.</p><button onClick={() => navigate('/dashboard/payments')}>Go Back</button></div>;
    
    const paymentEntries = receipt.paymentEntries || [{
        id: 1, amount: receipt.amount, method: receipt.method, remarks: receipt.chequeNo || receipt.remarks 
    }];
    const totalAmount = receipt.totalAmount || receipt.amount;

    const displayDate = receipt.date 
        ? receipt.date 
        : (receipt.paymentTimestamp ? new Date(receipt.paymentTimestamp.toDate()).toLocaleDateString('en-GB') : 'N/A');

    // 👇 নতুন লজিক: Amount Due Date এর লেবেল দেখানোর জন্য
    const getAmountDueLabel = () => {
        if (receipt.amountDueDate) {
            return `AMOUNT DUE @ ${receipt.amountDueDate}`;
        }
        return "AMOUNT DUE";
    };

    return (
        <div className="view-invoice-container">
            <div className="invoice-actions no-print">
                <button 
                    onClick={() => navigate('/dashboard/payments')}
                    style={{backgroundColor: '#6c757d', marginRight: '10px', display: 'flex', alignItems: 'center', gap: '5px'}}
                >
                    <FaArrowLeft /> Back
                </button>
                
                <button 
                    onClick={handleShare} 
                    disabled={isSharing}
                    style={{backgroundColor: '#e67e22', marginRight: '10px', display: 'inline-flex', alignItems: 'center', gap: '5px'}}
                >
                    <FaShareAlt /> {isSharing ? 'Processing...' : 'Share Receipt'}
                </button>

                <button 
                    onClick={() => window.print()}
                    style={{display: 'flex', alignItems: 'center', gap: '5px'}}
                >
                    <FaPrint /> Print Receipt
                </button>
            </div>

            <div className="invoice-paper" style={{minHeight: 'auto'}}>
                <div className="invoice-header">
                    <h1>TWS AIR INTERNATIONAL</h1>
                    <p>10/3/7 Toyenbee Circular Road, Motijheel C/A, Dhaka-1000, BANGLADESH.</p>
                    <h2 className="receipt-title">MONEY RECEIPT</h2>
                </div>
                
                <div className="customer-details">
                    <div>
                        <p><strong>Received From:</strong> {receipt.companyName}</p>
                        <p><strong>Address:</strong> {receipt.address}</p>
                    </div>
                    <div>
                        <p><strong>Receipt No:</strong> <span style={{color: 'red', fontWeight: 'bold'}}>{receipt.receiptNo}</span></p>
                        <p><strong>Receipt Date:</strong> {displayDate}</p>
                    </div>
                </div>

                <table className="invoice-items-table">
                    <thead>
                        <tr>
                            <th>Details</th>
                            <th>Amount (BDT)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentEntries.map((entry, index) => (
                            <tr key={index}>
                                <td style={{textAlign: 'left', padding: '10px'}}>
                                    Payment Method: <strong>{entry.method}</strong>
                                    {(entry.remarks) && (
                                        <p style={{fontSize: '11px', color: '#555', margin: '5px 0 0 0'}}>
                                            <em>Remarks: {entry.remarks}</em>
                                        </p>
                                    )}
                                </td>
                                <td style={{textAlign: 'right', padding: '10px', fontWeight: 'bold', fontSize: '14px'}}>
                                    {formatCurrency(entry.amount)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="invoice-summary-view" style={{marginTop: '30px'}}>
                    <div className="summary-left">
                        <div className="in-word-container">
                            <strong>IN WORD: </strong>
                            <span>{numberToWords(totalAmount)}</span>
                        </div>
                    </div>
                    <div className="summary-right" style={{width: '40%'}}>
                        <p className="final-total-view">
                            <span>Total Received</span> <span>{formatCurrency(totalAmount)}</span>
                        </p>
                        
                        {/* --- AMOUNT DUE SECTION UPDATED --- */}
                        {liveInvoice ? (
                            <p className="final-total-view amount-due-view">
                                {/* 👇 এখানে ডায়নামিক লেবেল ব্যবহার করা হয়েছে */}
                                <span>{getAmountDueLabel()}</span> 
                                <span>{formatCurrency(liveInvoice.finalTotalBdt)}</span>
                            </p>
                        ) : (
                            receipt.finalTotalBdt !== undefined && (
                                <p className="final-total-view amount-due-view">
                                    {/* 👇 এখানে ডায়নামিক লেবেল ব্যবহার করা হয়েছে */}
                                    <span>{getAmountDueLabel()}</span> 
                                    <span>{formatCurrency(receipt.finalTotalBdt)}</span>
                                </p>
                            )
                        )}
                    </div>
                </div>
                
                <div className="receipt-signature-area">
                    <div className="signature-box">
                        <p>-----------------------------</p>
                        <p><strong>Receiver's Signature</strong></p>
                    </div>
                    
                    <div className="signature-box seal-box">
                        <img src={companyLogo} alt="Company Logo" className="receipt-company-logo" />
                    </div>
                    
                    <div className="signature-box authorized-signature-box">
                        {receipt.authorizedBy === 'Anis Ahmed' && (
                            <img src={signatureAnis} alt="Anis Ahmed Signature" className="receipt-signature-img" />
                        )}
                        {receipt.authorizedBy === 'MD. Morshed' && (
                            <img src={signatureMorshed} alt="MD. Morshed Signature" className="receipt-signature-img" />
                        )}
                        
                        <p className="signature-line">-----------------------------</p>
                        <p><strong>Authorized Signature</strong></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewReceipt;